import { z } from 'zod';
import type { AdminChatSkillSummary } from './skill';

export const CHAT_CONFIG_SNAPSHOT_SCHEMA_VERSION = 1 as const;
export const CHAT_CONFIG_RUNTIME_CURRENT_KV_KEY = 'chat:runtime:current';
export const CHAT_CONFIG_RUNTIME_POINTER_RECEIPT_KV_KEY = 'chat:runtime:current:receipt';
export const CHAT_CONFIG_RUNTIME_VERSION_KV_PREFIX = 'chat:runtime:version:';
export const CHAT_SKILL_SELECTOR_TASK_TYPE = 'chat_skill_selector' as const;
export const CHAT_SKILL_SELECTOR_INPUT_CONTRACT_VERSION = 'v1' as const;
export const CHAT_SKILL_SELECTOR_OUTPUT_CONTRACT_VERSION = 'v1' as const;
export const CHAT_SKILL_SELECTOR_AUDIT_SCHEMA_VERSION = 1 as const;
export const CHAT_SELECTOR_CONTEXT_MAX_MESSAGES = 8;
export const CHAT_SELECTOR_CONTEXT_MAX_BYTES = 16_000;
export const CHAT_SELECTOR_MAX_OUTPUT_TOKENS = 128;
export const CHAT_SELECTOR_TIMEOUT_MS = 60_000;

const ChatConfigIdentifier = z.string().trim().min(1).max(128);
const ChatSkillSelectorSkillId = z.string().min(1).max(128);
const Sha256Hex = z.string().regex(/^[0-9a-f]{64}$/);

export const ChatConfigDraftRevision = z.number().int().min(0);
export type ChatConfigDraftRevision = z.infer<typeof ChatConfigDraftRevision>;

export const AdminChatConfigDraftUpdateRequest = z
  .object({
    expected_draft_revision: ChatConfigDraftRevision,
    base_prompt: z.string().trim().min(1).max(200_000).optional(),
    selector_model_id: z.string().trim().min(1).max(128).optional(),
  })
  .strict()
  .refine((value) => value.base_prompt !== undefined || value.selector_model_id !== undefined, {
    message: 'at least one draft field is required',
  });
export type AdminChatConfigDraftUpdateRequest = z.infer<typeof AdminChatConfigDraftUpdateRequest>;

export const AdminChatConfigSkillStatusRequest = z
  .object({
    expected_draft_revision: ChatConfigDraftRevision,
    status: z.enum(['draft', 'active']),
  })
  .strict();
export type AdminChatConfigSkillStatusRequest = z.infer<typeof AdminChatConfigSkillStatusRequest>;

export const AdminChatConfigSkillArchiveRequest = z
  .object({ expected_draft_revision: ChatConfigDraftRevision })
  .strict();
export type AdminChatConfigSkillArchiveRequest = z.infer<typeof AdminChatConfigSkillArchiveRequest>;

export const AdminChatConfigSkillOrderRequest = z
  .object({
    expected_draft_revision: ChatConfigDraftRevision,
    skill_ids: z.array(z.string().trim().min(1).max(128)).max(100),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (new Set(value.skill_ids).size === value.skill_ids.length) return;
    ctx.addIssue({
      code: 'custom',
      path: ['skill_ids'],
      message: 'skill_ids must be unique',
    });
  });
export type AdminChatConfigSkillOrderRequest = z.infer<typeof AdminChatConfigSkillOrderRequest>;

export type AdminChatConfigDraftSkill = {
  position: number;
  bound_revision: number;
  skill: AdminChatSkillSummary;
};

export type AdminChatConfigDraft = {
  draft_revision: number;
  base_prompt: string | null;
  base_prompt_sha256: string | null;
  selector_model_id: string | null;
  current_version_id: string | null;
  skills: AdminChatConfigDraftSkill[];
  created_at: string;
  updated_at: string;
};

export const AdminChatConfigPublishRequest = z
  .object({
    client_request_id: z.string().uuid(),
    expected_current_version: ChatConfigIdentifier.nullable(),
    expected_draft_revision: ChatConfigDraftRevision,
  })
  .strict();
export type AdminChatConfigPublishRequest = z.infer<typeof AdminChatConfigPublishRequest>;

export const AdminChatConfigRepublishRequest = AdminChatConfigPublishRequest;
export type AdminChatConfigRepublishRequest = z.infer<typeof AdminChatConfigRepublishRequest>;

export const AdminChatConfigCacheRebuildRequest = z
  .object({ client_request_id: z.string().uuid() })
  .strict();
export type AdminChatConfigCacheRebuildRequest = z.infer<typeof AdminChatConfigCacheRebuildRequest>;

export const AdminChatConfigVersionListQuery = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type AdminChatConfigVersionListQuery = z.infer<typeof AdminChatConfigVersionListQuery>;

export const ChatConfigSnapshotSkill = z
  .object({
    position: z.number().int().min(0),
    skill_id: ChatConfigIdentifier,
    revision: z.number().int().min(1),
    name: z.string().min(1).max(80),
    summary: z.string().min(1).max(256),
    applicability: z.string().min(1).max(2_000),
    instructions: z.string().min(1).max(200_000),
  })
  .strict();
export type ChatConfigSnapshotSkill = z.infer<typeof ChatConfigSnapshotSkill>;

export const ChatSkillSelectorDirectoryEntry = ChatConfigSnapshotSkill.pick({
  skill_id: true,
  name: true,
  summary: true,
  applicability: true,
}).strict();
export type ChatSkillSelectorDirectoryEntry = z.infer<typeof ChatSkillSelectorDirectoryEntry>;

export const ChatSkillSelectorMessage = z
  .object({
    role: z.enum(['user', 'assistant']),
    content: z.string().min(1),
  })
  .strict();
export type ChatSkillSelectorMessage = z.infer<typeof ChatSkillSelectorMessage>;

export const ChatSkillSelectorInput = z
  .object({
    skills: z.array(ChatSkillSelectorDirectoryEntry).max(100),
    messages: z.array(ChatSkillSelectorMessage).min(1).max(CHAT_SELECTOR_CONTEXT_MAX_MESSAGES),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.messages.at(-1)?.role !== 'user') {
      context.addIssue({
        code: 'custom',
        path: ['messages'],
        message: 'selector context must end with the submitted user message',
      });
    }
    const byteLength = value.messages.reduce(
      (total, message) => total + utf8ByteLength(message.content),
      0,
    );
    if (byteLength > CHAT_SELECTOR_CONTEXT_MAX_BYTES) {
      context.addIssue({
        code: 'too_big',
        maximum: CHAT_SELECTOR_CONTEXT_MAX_BYTES,
        origin: 'array',
        inclusive: true,
        path: ['messages'],
        message: 'selector message text exceeds the UTF-8 byte limit',
      });
    }
  });
export type ChatSkillSelectorInput = z.infer<typeof ChatSkillSelectorInput>;

export const ChatSkillSelectorOutput = z
  .object({
    skill_ids: z.array(ChatSkillSelectorSkillId).max(3),
  })
  .strict()
  .superRefine((value, context) => {
    if (new Set(value.skill_ids).size === value.skill_ids.length) return;
    context.addIssue({
      code: 'custom',
      path: ['skill_ids'],
      message: 'skill_ids must be unique',
    });
  });
export type ChatSkillSelectorOutput = z.infer<typeof ChatSkillSelectorOutput>;

export const CHAT_SKILL_SELECTOR_OUTPUT_JSON_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  properties: {
    skill_ids: {
      type: 'array',
      items: { type: 'string', minLength: 1, maxLength: 128 },
      maxItems: 3,
      uniqueItems: true,
    },
  },
  required: ['skill_ids'],
} as const satisfies Record<string, unknown>;

export const ChatSkillSelectorAuditOutcome = z.enum([
  'selected',
  'empty',
  'failed',
  'invalid',
  'uncertain',
  'not_run',
]);
export type ChatSkillSelectorAuditOutcome = z.infer<typeof ChatSkillSelectorAuditOutcome>;

export const ChatSkillSelectorAudit = z
  .object({
    schema_version: z.literal(CHAT_SKILL_SELECTOR_AUDIT_SCHEMA_VERSION),
    input_contract_version: z.literal(CHAT_SKILL_SELECTOR_INPUT_CONTRACT_VERSION),
    output_contract_version: z.literal(CHAT_SKILL_SELECTOR_OUTPUT_CONTRACT_VERSION),
    chat_config_version_id: ChatConfigIdentifier,
    snapshot_sha256: Sha256Hex,
    outcome: ChatSkillSelectorAuditOutcome,
    skill_ids: z.array(ChatSkillSelectorSkillId).max(3),
  })
  .strict()
  .superRefine((value, context) => {
    if (new Set(value.skill_ids).size !== value.skill_ids.length) {
      context.addIssue({
        code: 'custom',
        path: ['skill_ids'],
        message: 'skill_ids must be unique',
      });
    }
    if (value.outcome === 'selected' ? value.skill_ids.length > 0 : value.skill_ids.length === 0) {
      return;
    }
    context.addIssue({
      code: 'custom',
      path: ['skill_ids'],
      message: 'only selected outcomes may contain skill ids',
    });
  });
export type ChatSkillSelectorAudit = z.infer<typeof ChatSkillSelectorAudit>;

function utf8ByteLength(value: string): number {
  let bytes = 0;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && index + 1 < value.length) {
      const next = value.charCodeAt(index + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 4;
        index += 1;
      } else {
        bytes += 3;
      }
    } else {
      bytes += 3;
    }
  }
  return bytes;
}

export const ChatConfigSnapshot = z
  .object({
    schema_version: z.literal(CHAT_CONFIG_SNAPSHOT_SCHEMA_VERSION),
    version_id: ChatConfigIdentifier,
    draft_revision: ChatConfigDraftRevision,
    base_prompt: z.string().min(1).max(200_000),
    base_prompt_sha256: Sha256Hex,
    selector_model_id: ChatConfigIdentifier,
    skills: z.array(ChatConfigSnapshotSkill).max(100),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.skills.every((skill, index) => skill.position === index)) return;
    ctx.addIssue({
      code: 'custom',
      path: ['skills'],
      message: 'skills must have contiguous ordered positions',
    });
  });
export type ChatConfigSnapshot = z.infer<typeof ChatConfigSnapshot>;

export const ChatConfigRuntimePointer = z
  .object({
    schema_version: z.literal(CHAT_CONFIG_SNAPSHOT_SCHEMA_VERSION),
    version_id: ChatConfigIdentifier,
    snapshot_sha256: Sha256Hex,
    key: z.string().min(1).max(300),
    write_id: z.string().uuid(),
  })
  .strict();
export type ChatConfigRuntimePointer = z.infer<typeof ChatConfigRuntimePointer>;

export const ChatConfigRuntimePointerReceipt = ChatConfigRuntimePointer.pick({
  schema_version: true,
  version_id: true,
  snapshot_sha256: true,
  write_id: true,
}).strict();
export type ChatConfigRuntimePointerReceipt = z.infer<typeof ChatConfigRuntimePointerReceipt>;

export const ChatConfigPublicationOperationKind = z.enum(['publish', 'republish', 'rebuild']);
export type ChatConfigPublicationOperationKind = z.infer<typeof ChatConfigPublicationOperationKind>;

export const ChatConfigPublicationOperationStatus = z.enum([
  'prepared',
  'committing',
  'reconcile_required',
  'completed',
  'failed',
]);
export type ChatConfigPublicationOperationStatus = z.infer<
  typeof ChatConfigPublicationOperationStatus
>;
